import React, { useState, useEffect } from 'react';
import { Clock, Trophy, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import Button from '../shared/Button';
import ProgramRegistrationModal from './ProgramRegistrationModal';
import axiosInstance from '../../api/axiosInstance';
import useHomePageContent from '../../hooks/useHomePageContent';

const ProgramDetailsModal = ({ program, onClose, onRegisterClick }) => {
  const features = [
    { text: program.feature1, color: program.feature1_color },
    { text: program.feature2, color: program.feature2_color },
    { text: program.feature3, color: program.feature3_color },
    { text: program.feature4, color: program.feature4_color },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full relative overflow-hidden">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full transition-colors">
          <X className="w-6 h-6 text-gray-400" />
        </button>

        <div className="bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] p-6 text-white">
          <h3 className="text-2xl font-bold mb-2">{program.title}</h3>
          <div className="flex items-center text-white/90">
            <Clock className="w-4 h-4 mr-2" />
            <span>{program.duration}</span>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {program.about_program && (
            <div>
              <h4 className="text-xl font-bold mb-3">{program.about_program_title || 'О программе'}</h4>
              <p className="text-gray-600">{program.about_program}</p>
            </div>
          )}

          <div>
            <h4 className="text-xl font-bold mb-3">{program.features_title || 'Особенности программы'}</h4>
            <ul className="space-y-2">
              {features.map((feature, index) =>
                feature.text ? (
                  <li key={index} className="flex items-center">
                    <div className="w-2 h-2 rounded-full mr-3" style={{ backgroundColor: feature.color }} />
                    <span className="text-gray-600">{feature.text}</span>
                  </li>
                ) : null,
              )}
            </ul>
          </div>

          <div className="pt-6 border-t">
            <Button onClick={onRegisterClick} className="w-full" withArrow>
              {program.registration_button_text || 'Записаться на консультацию'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgramCard = ({ program }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);

  const features = [
    { text: program.feature1, color: '#662d91' },
    { text: program.feature2, color: '#4897d2' },
  ];

  return (
    <>
      <div className="relative h-full">
        <div className="relative bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-8 h-full flex flex-col">
          <div className="w-14 h-14 rounded-lg bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] p-0.5">
            <div className="w-full h-full bg-white rounded-lg flex items-center justify-center">
              <Trophy className="w-8 h-8 text-[#662d91]" />
            </div>
          </div>

          <h3 className="text-2xl font-bold mt-4 mb-4 min-h-[2.5rem]">{program.title}</h3>

          <div className="mb-6 min-h-[4rem]">
            <ul className="space-y-3">
              {features.map((feature, index) =>
                feature.text ? (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 rounded-full mr-3 mt-2" style={{ backgroundColor: feature.color }} />
                    <span className="text-sm text-gray-600 leading-relaxed">{feature.text}</span>
                  </li>
                ) : null,
              )}
            </ul>
          </div>

          <div className="mt-auto">
            <Button onClick={() => setShowDetails(true)} withArrow className="w-full">
              Подробнее
            </Button>
          </div>
        </div>
      </div>

      {showDetails && (
        <ProgramDetailsModal
          program={program}
          onClose={() => setShowDetails(false)}
          onRegisterClick={() => {
            setShowDetails(false);
            setShowRegistration(true);
          }}
        />
      )}

      {showRegistration && <ProgramRegistrationModal program={program} onClose={() => setShowRegistration(false)} />}
    </>
  );
};

const ProgramsSection = () => {
  const { content } = useHomePageContent();
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const response = await axiosInstance.get('trainings/');
        const filtered = (response.data || []).filter((p) => p.slug !== 'power-training');
        setPrograms(filtered);
      } catch (err) {
        console.error('Error fetching programs:', err);
        setError('Failed to load programs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#662d91]"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-center text-red-600 py-8">Error: {error}</div>;
  }

  return (
    <section id="programs" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4">
            {content.programs_section_title}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {content.programs_section_description}
          </p>
          <div className="mt-5">
            <Link to="/programs" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4]">Все программы</Link>
          </div>
          <div className="w-24 h-1 bg-gradient-to-r from-[#662d91] via-[#4897d2] to-[#28c4f4] mx-auto mt-6"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {programs.map((program) => (
            <ProgramCard key={program.id} program={program} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProgramsSection;
